import React from 'react';
import { useTranslation } from 'react-i18next';
import { CreditCard, Building2, Wallet, Clock, Smartphone } from 'lucide-react';
import ContentPageLayout from '../components/ContentPageLayout';

const Zahlungsarten = () => {
  const { t } = useTranslation();

  return (
    <ContentPageLayout>
      {/* Zahlungsarten Hauptbereich */}
      <section className="py-6 bg-white" data-testid="zahlungsarten-page">
        <div className="container mx-auto px-4 sm:px-6 lg:px-2 xl:max-w-[1800px]">
          
          {/* Überschrift */}
          <h1 className="text-lg sm:text-xl font-semibold text-white bg-gray-900 px-6 py-3 rounded-t-lg flex items-center" data-testid="zahlungsarten-title">
            <CreditCard className="mr-3" size={24} />
            {t('paymentPage.title')}
          </h1>

          {/* Textbereich */}
          <div className="bg-gray-50 rounded-b-xl border border-gray-200 border-t-0 p-6 md:p-8">
                
            <p className="text-base sm:text-lg text-gray-700 leading-relaxed mb-6">
              {t('paymentPage.intro')}
            </p>

            {/* Zahlungsarten-Logos im einheitlichen Design - Hybrid: Inline SVG + Mollie CDN */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-4 mb-10">
              {/* Visa - Inline SVG */}
              <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center justify-center h-20 hover:border-red-300 hover:shadow-sm transition-all" title="Visa">
                <svg viewBox="0 0 750 471" className="h-10 w-auto">
                  <path d="M278.198 334.228l33.36-195.763h53.358l-33.384 195.763h-53.334zm246.11-191.54c-10.57-3.966-27.135-8.222-47.822-8.222-52.725 0-89.863 26.551-90.18 64.604-.297 28.129 26.515 43.822 46.754 53.185 20.77 9.597 27.752 15.716 27.654 24.283-.133 13.123-16.586 19.116-31.924 19.116-21.355 0-32.701-2.967-50.225-10.274l-6.878-3.111-7.487 43.822c12.463 5.466 35.508 10.199 59.438 10.445 56.09 0 92.502-26.248 92.916-66.884.199-22.27-14.016-39.216-44.801-53.188-18.65-9.056-30.072-15.099-29.951-24.269 0-8.137 9.668-16.838 30.56-16.838 17.446-.271 30.089 3.527 39.936 7.496l4.781 2.26 7.229-42.425m137.307-4.223h-41.232c-12.773 0-22.332 3.486-27.94 16.234l-79.245 179.404h56.031s9.159-24.121 11.231-29.418c6.123 0 60.555.084 68.336.084 1.596 6.854 6.492 29.334 6.492 29.334h49.512l-43.185-195.638zm-65.417 126.408c4.414-11.279 21.26-54.724 21.26-54.724-.314.521 4.381-11.334 7.074-18.684l3.607 16.878s10.217 46.729 12.352 56.527h-44.293v.003zm-363.667-126.408l-52.24 133.496-5.567-27.129c-9.726-31.274-40.025-65.157-73.898-82.12l47.767 171.203 56.455-.063 84.004-195.386-56.521-.001" fill="#1A1F71"/>
                  <path d="M146.92 138.465H60.879l-.682 4.073c66.939 16.204 111.232 55.363 129.618 102.415l-18.709-89.96c-3.229-12.396-12.597-16.095-24.186-16.528" fill="#F9A533"/>
                </svg>
              </div>
              
              {/* Mastercard - Neues offizielles Logo */}
              <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center justify-center h-20 hover:border-red-300 hover:shadow-sm transition-all" title="Mastercard">
                <img src="https://customer-assets.emergentagent.com/job_c8ae81ef-39b2-4170-b30f-e165111bc7d2/artifacts/gwxfm1bl_MasterCard_Logo.svg.png" alt="Mastercard" className="h-10 w-auto object-contain" />
              </div>
              
              {/* American Express - Korrektes AMEX Logo */}
              <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center justify-center h-20 hover:border-red-300 hover:shadow-sm transition-all" title="American Express">
                <svg viewBox="0 0 60 40" className="h-10 w-auto">
                  <rect width="60" height="40" rx="4" fill="#016FD0"/>
                  <text x="30" y="26" textAnchor="middle" fill="white" fontSize="12" fontFamily="Arial, sans-serif" fontWeight="bold">AMEX</text>
                </svg>
              </div>
              
              {/* Apple Pay - Mollie CDN */}
              <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center justify-center h-20 hover:border-red-300 hover:shadow-sm transition-all" title="Apple Pay">
                <img src="https://www.mollie.com/external/icons/payment-methods/applepay.svg" alt="Apple Pay" className="h-10 w-auto" />
              </div>
              
              {/* PayPal - Neues offizielles Logo */}
              <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center justify-center h-20 hover:border-red-300 hover:shadow-sm transition-all" title="PayPal">
                <img src="https://customer-assets.emergentagent.com/job_c8ae81ef-39b2-4170-b30f-e165111bc7d2/artifacts/529c93tl_Paypal_2014_logo.png" alt="PayPal" className="h-10 w-auto object-contain" />
              </div>
              
              {/* Klarna - Mollie CDN */}
              <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center justify-center h-20 hover:border-red-300 hover:shadow-sm transition-all" title="Klarna">
                <img src="https://www.mollie.com/external/icons/payment-methods/klarna.svg" alt="Klarna" className="h-10 w-auto" />
              </div>
              
              {/* SEPA Bank Transfer - Mollie CDN */}
              <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center justify-center h-20 hover:border-red-300 hover:shadow-sm transition-all" title="SEPA Überweisung">
                <img src="https://www.mollie.com/external/icons/payment-methods/banktransfer.svg" alt="SEPA" className="h-10 w-auto" />
              </div>
            </div>

            {/* Abschnitt 1: Kreditkarte */}
            <div className="mb-8 pb-8 border-b border-gray-200">
              <h2 className="text-base sm:text-lg font-bold text-white bg-red-600 px-4 py-2 rounded-lg inline-flex items-center mb-4">
                <CreditCard className="mr-2" size={20} />
                Kredit- & Debitkarte
              </h2>
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed mb-3">
                Bezahlen Sie bequem und sicher mit Ihrer <strong>Visa</strong>, <strong>Mastercard</strong> oder <strong>American Express</strong>. 
                Die Zahlung erfolgt über den zertifizierten Zahlungsdienstleister Mollie mit SSL-Verschlüsselung.
              </p>
              <ul className="text-sm text-gray-600 space-y-1 ml-4 list-disc">
                <li>Sofortige Zahlungsbestätigung</li>
                <li>Schneller Versand nach Zahlungseingang</li>
                <li>Ihre Kartendaten werden nicht bei uns gespeichert</li>
              </ul>
            </div>

            {/* Abschnitt 2: Apple Pay */}
            <div className="mb-8 pb-8 border-b border-gray-200">
              <h2 className="text-base sm:text-lg font-bold text-white bg-red-600 px-4 py-2 rounded-lg inline-flex items-center mb-4">
                <Smartphone className="mr-2" size={20} />
                Apple Pay
              </h2>
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed mb-3">
                Mit <strong>Apple Pay</strong> bezahlen Sie schnell und sicher per Touch ID oder Face ID. 
                Die Zahlung ist in Sekunden abgeschlossen – ohne Kartendaten eingeben zu müssen.
              </p>
              <ul className="text-sm text-gray-600 space-y-1 ml-4 list-disc">
                <li>Schnelle Authentifizierung per Fingerabdruck oder Gesichtserkennung</li>
                <li>Höchste Sicherheitsstandards</li>
                <li>Verfügbar auf iPhone, iPad, Mac und Apple Watch</li>
              </ul>
            </div>

            {/* Abschnitt 3: PayPal */}
            <div className="mb-8 pb-8 border-b border-gray-200">
              <h2 className="text-base sm:text-lg font-bold text-white bg-red-600 px-4 py-2 rounded-lg inline-flex items-center mb-4">
                <Wallet className="mr-2" size={20} />
                PayPal
              </h2>
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed mb-3">
                Mit <strong>PayPal</strong> bezahlen Sie schnell, sicher und bequem – ohne Ihre Bankdaten direkt anzugeben. 
                Sie werden während des Bestellvorgangs zu PayPal weitergeleitet.
              </p>
              <ul className="text-sm text-gray-600 space-y-1 ml-4 list-disc">
                <li>PayPal-Käuferschutz inklusive</li>
                <li>Zahlung mit PayPal-Guthaben, Bankkonto oder Kreditkarte</li>
                <li>Sofortige Zahlungsbestätigung</li>
              </ul>
            </div>

            {/* Abschnitt 4: Klarna */}
            <div className="mb-8 pb-8 border-b border-gray-200">
              <h2 className="text-base sm:text-lg font-bold text-white bg-red-600 px-4 py-2 rounded-lg inline-flex items-center mb-4">
                <Clock className="mr-2" size={20} />
                Klarna – Rechnung & Ratenzahlung
              </h2>
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed mb-3">
                Mit <strong>Klarna</strong> haben Sie flexible Zahlungsmöglichkeiten: Kaufen Sie jetzt und bezahlen Sie später per Rechnung, 
                oder teilen Sie den Betrag in bequeme Raten auf.
              </p>
              <ul className="text-sm text-gray-600 space-y-1 ml-4 list-disc">
                <li><strong>Rechnung:</strong> Bezahlen Sie innerhalb von 14-30 Tagen</li>
                <li><strong>Ratenzahlung:</strong> Teilen Sie den Betrag in monatliche Raten auf</li>
                <li>Schnelle Bonitätsprüfung bei Klarna</li>
                <li>Käuferschutz inklusive</li>
              </ul>
              <div className="bg-pink-50 border border-pink-200 rounded-lg p-3 mt-3">
                <p className="text-sm text-pink-800">
                  <strong>Hinweis:</strong> Die Verfügbarkeit von Klarna hängt von der Bonitätsprüfung ab. Bei gewerblichen Kunden gelten ggf. andere Konditionen.
                </p>
              </div>
            </div>

            {/* Abschnitt 5: SEPA-Überweisung */}
            <div className="mb-8 pb-8 border-b border-gray-200">
              <h2 className="text-base sm:text-lg font-bold text-white bg-red-600 px-4 py-2 rounded-lg inline-flex items-center mb-4">
                <Building2 className="mr-2" size={20} />
                SEPA-Überweisung (Vorkasse)
              </h2>
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed mb-3">
                Die klassische <strong>Banküberweisung</strong> ist eine sichere und bewährte Zahlungsmethode. 
                Nach Ihrer Bestellung erhalten Sie eine E-Mail mit unseren Bankdaten. 
                Sobald der Betrag eingegangen ist, wird Ihre Bestellung bearbeitet.
              </p>
              <div className="bg-white rounded-lg border border-gray-200 p-4 mb-3">
                <p className="text-sm text-gray-600 font-medium mb-3">Unsere Bankverbindung:</p>
                <div className="space-y-1">
                  <p className="text-sm text-gray-700"><span className="font-medium">Kontoinhaber:</span> JANNESMANN HANDELS UND IMPORT GbR</p>
                  <p className="text-sm text-gray-700"><span className="font-medium">IBAN:</span> DE23 4245 1220 0000 0076 33</p>
                  <p className="text-sm text-gray-700"><span className="font-medium">BIC:</span> WELADED1BOT</p>
                </div>
              </div>
              <ul className="text-sm text-gray-600 space-y-1 ml-4 list-disc">
                <li>Keine Zusatzkosten für die Überweisung</li>
                <li>Versand nach Zahlungseingang</li>
                <li>Bitte geben Sie die Bestellnummer als Verwendungszweck an</li>
              </ul>
            </div>

            {/* Abschnitt 6: Manuelle Überweisung */}
            <div>
              <h2 className="text-base sm:text-lg font-bold text-white bg-red-600 px-4 py-2 rounded-lg inline-flex items-center mb-4">
                <Wallet className="mr-2" size={20} />
                Manuelle Überweisung (Vorkasse per Rechnung)
              </h2>
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed mb-3">
                Wählen Sie diese Option, wenn Sie nach Bestellabschluss eine <strong>Rechnung mit Bankdaten</strong> per E-Mail erhalten möchten. 
                Diese Zahlungsart eignet sich besonders für höhere Beträge oder wenn Sie die Zahlung intern freigeben lassen müssen.
              </p>
              <ul className="text-sm text-gray-600 space-y-1 ml-4 list-disc">
                <li>Rechnung wird automatisch per E-Mail versendet</li>
                <li>Ideal für gewerbliche Kunden mit internen Freigabeprozessen</li>
                <li>Versand nach Zahlungseingang</li>
              </ul>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-3">
                <p className="text-sm text-blue-800">
                  <strong>Tipp:</strong> Bei Fragen zu individuellen Zahlungsvereinbarungen kontaktieren Sie uns gerne telefonisch unter <strong>02041 9877499</strong> oder per E-Mail an <strong>info@jannesmann.de</strong>.
                </p>
              </div>
            </div>

          </div>
        </div>
      </section>
    </ContentPageLayout>
  );
};

export default Zahlungsarten;
